import { useMemo, useRef } from "react";
import { createAirUpdateCardHook, useAirCardActions } from "features/AirCardActions";
import { cardToDeleteStore } from "features/CardToDelete";
import { filteredCardsStore } from "features/Filter";
import { LoadMoreButton } from "features/LoadMoreButton";
import { openedCardStore } from "features/OpenedCard";
import { useCardHandlers, useMainViewScrollRestoration } from "features/Panel/hooks/Panel.hooks";
import { useAirSearch } from "features/Search";
import { usePanelViews } from "features/shared/usePanelViews.hook";
import { tagsStore, useTagsContext } from "features/Tags";
import { AirSavedCard, airSavedCardStore, useAirInfiniteScrollFetch } from "entities/AirSavedCard";
import { AirSearchCard, airSearchCardStore } from "entities/AirSearchCard";
import { authStore } from "entities/Auth";
import { Card, ErrorCard, LoadCard } from "entities/Card";
import { PanelView } from "entities/PanelView";
import { searchFilterStore } from "entities/SearchFilter";
import { mainViewId, savedShipmentLoadingText, searchShipmentLoadingText } from "shared/constants/panel.constants";
import { useAppDispatch, useAppSelector } from "shared/lib/redux";
import { useTranslation } from "shared/localization/localization";
export const AIR_QUERY_CACHE_KEY = "get_air_saved_cards_infinite_query";

type AirViewProps = {
  className?: string;
};

export const AirView: RFC<AirViewProps> = ({ className }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const airSearchCards = useAppSelector(airSearchCardStore.selectors.searchCards);
  const airSavedCards = useAppSelector(airSavedCardStore.selectors.savedCards);
  const visibleAirSearchCards = useMemo(() => {
    const savedReferences = new Set(airSavedCards.map((card) => card.reference));
    return airSearchCards.filter((card) => !card.reference || !savedReferences.has(card.reference));
  }, [airSearchCards, airSavedCards]);
  const isAuthenticated = useAppSelector(authStore.selectors.isAuthenticated);
  const openedCard = useAppSelector(openedCardStore.selectors.openedCard);
  const shipmentType = useAppSelector(searchFilterStore.selectors.shipment);
  const { showIntroView } = usePanelViews();
  const { isAirSearchLoading } = useAirSearch({ isAuthenticated });
  const { openCard } = useCardHandlers({
    openedCardAction: openedCardStore.actions.setOpenedCardAction,
  });
  const { refreshTags } = useTagsContext();
  useMainViewScrollRestoration(containerRef);

  const {
    deleteAirCardHandler,
    airCardFollow,
    isAirCardFollowButtonVisible,
    isAirCardFollowLoading,
    isAirCardDeleting,
  } = useAirCardActions({
    setCardToDeleteAction: (card) => ({
      type: cardToDeleteStore.actions.setCardToDeleteAction.type,
      payload: card,
    }),
    resetCardToDeleteAction: () => ({
      type: cardToDeleteStore.actions.resetCardToDeleteAction.type,
    }),
    removeFilteredCardAction: (card) => ({
      type: filteredCardsStore.actions.removeFilteredCardAction.type,
      payload: card,
    }),
    removeCardByTagAction: (card) => ({
      type: tagsStore.actions.removeCardByTagAction.type,
      payload: card,
    }),
    refreshTags,
  });

  const { requestAirMoreCards, shouldRenderAirInfiniteQueryLoader, shouldRenderAirMoreButton } =
    useAirInfiniteScrollFetch({
      containerRef,
      queryKey: [AIR_QUERY_CACHE_KEY],
      onSuccess: ({ page }) => {
        dispatch(airSavedCardStore.actions.appendSavedCardsAction({ data: page }));
      },
      onSettled: ({ page }) => {
        // Keep the search result list visible — an empty saved-cards response must not
        // bounce intro ↔ main and re-trigger the saved-cards request in a loop.
        if (!page?.length && !airSearchCards.length) {
          showIntroView();
        }
      },
      // Panel already asked on mount; this instance only pages through the rest.
      shouldRequestOnMount: false,
    });

  return (
    <PanelView
      ref={containerRef}
      className={className}
      id={mainViewId}
      shouldRenderMoreButton={shouldRenderAirMoreButton}
      loadMoreButton={<LoadMoreButton onClick={requestAirMoreCards} />}
    >
      {isAirSearchLoading && <LoadCard text={t(searchShipmentLoadingText)} />}

      {visibleAirSearchCards.map((card, index) => (
        <AirSearchCard
          key={card.reference ?? index}
          data={card}
          isAuthenticated={isAuthenticated}
          openCard={openCard}
          deleteCardHandler={deleteAirCardHandler}
          follow={airCardFollow}
          isFollowButtonVisible={isAirCardFollowButtonVisible}
          isFollowLoading={isAirCardFollowLoading}
          shipmentType={shipmentType}
          renderCard={(props) => <Card {...props} />}
          renderErrorCard={(props) => <ErrorCard {...props} />}
        />
      ))}

      {isAuthenticated && (
        <>
          {airSavedCards.map((card) => (
            <AirSavedCard
              key={card.reference}
              data={card}
              openCard={openCard}
              isSavedCardDeleting={isAirCardDeleting}
              useSavedCardUpdateActions={createAirUpdateCardHook({
                model: card,
                isOpenedCardExist: !!openedCard,
                openedCardUpdateAction: (response) => ({
                  type: openedCardStore.actions.updateOpenCardAction.type,
                  payload: response,
                }),
                updateFilteredCards: (response) => ({
                  type: filteredCardsStore.actions.updateFilterCardAction.type,
                  payload: response,
                }),
                updateCardByTagAction: (response) => ({
                  type: tagsStore.actions.updateCardByTagAction.type,
                  payload: response,
                }),
              })}
              deleteCardHandler={deleteAirCardHandler}
              shipmentType={shipmentType}
              renderCard={(props) => <Card {...props} />}
            />
          ))}
          {shouldRenderAirInfiniteQueryLoader && <LoadCard text={t(savedShipmentLoadingText)} />}
        </>
      )}
    </PanelView>
  );
};
